import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Receipt, Wallet, FileText, Printer, Trash2, Inbox, BellRing, BarChart3, CheckCircle2, XCircle, Send } from "lucide-react";
import { AdminShell } from "@/components/admin/admin-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { requireSchoolAdminOrOnboard, useSchoolContext } from "@/lib/school-context";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { AICallout } from "@/components/makai/primitives";

export const Route = createFileRoute("/admin/fees")({
  ssr: false,
  beforeLoad: () => requireSchoolAdminOrOnboard(),
  head: () => ({ meta: [{ title: "Fees — MAKAI" }] }),
  component: FeesPage,
});

function FeesPage() {
  return (
    <AdminShell title="Fees" subtitle="Structures, invoices, payments and finance reports.">
      <Tabs defaultValue="invoices">
        <TabsList className="flex-wrap">
          <TabsTrigger value="invoices"><FileText className="mr-1.5 h-4 w-4" /> Invoices</TabsTrigger>
          <TabsTrigger value="payments"><Receipt className="mr-1.5 h-4 w-4" /> Payments</TabsTrigger>
          <TabsTrigger value="intents"><Inbox className="mr-1.5 h-4 w-4" /> Submitted</TabsTrigger>
          <TabsTrigger value="reminders"><BellRing className="mr-1.5 h-4 w-4" /> Reminders</TabsTrigger>
          <TabsTrigger value="structures"><Wallet className="mr-1.5 h-4 w-4" /> Fee items</TabsTrigger>
          <TabsTrigger value="reports"><BarChart3 className="mr-1.5 h-4 w-4" /> Reports</TabsTrigger>
        </TabsList>
        <TabsContent value="invoices" className="mt-6"><Invoices /></TabsContent>
        <TabsContent value="payments" className="mt-6"><Payments /></TabsContent>
        <TabsContent value="intents" className="mt-6"><Intents /></TabsContent>
        <TabsContent value="reminders" className="mt-6"><Reminders /></TabsContent>
        <TabsContent value="structures" className="mt-6"><Structures /></TabsContent>
        <TabsContent value="reports" className="mt-6"><Reports /></TabsContent>
      </Tabs>

      <div className="mt-6">
        <AICallout
          description="Predict cash flow, surface big debtors, and draft reminders."
          suggestions={[
            "Predict unpaid fees this term",
            "Top 10 outstanding balances",
            "Draft a payment reminder",
            "Cash-flow outlook for the next 30 days",
          ]}
        />
      </div>
    </AdminShell>
  );
}

const CATEGORIES = ["tuition","transport","meals","uniform","exam","boarding","development","activity","other"] as const;
const CATEGORY_LABEL: Record<string,string> = { tuition:"Tuition", transport:"Transport", meals:"Meals", uniform:"Uniform", exam:"Exam", boarding:"Boarding", development:"Development", activity:"Activity", other:"Other" };

// ============================ STRUCTURES ============================
function Structures() {
  const { active } = useSchoolContext();
  const [rows, setRows] = useState<any[]>([]);
  const [terms, setTerms] = useState<{ id: string; name: string }[]>([]);
  const [classes, setClasses] = useState<{ id: string; name: string }[]>([]);

  const load = useCallback(async () => {
    if (!active) return;
    const [s, t, c] = await Promise.all([
      supabase.from("fee_structures").select("*, term:terms(name), class:classes(name)").eq("school_id", active.school_id).order("created_at", { ascending: false }),
      supabase.from("terms").select("id,name").eq("school_id", active.school_id),
      supabase.from("classes").select("id,name").eq("school_id", active.school_id),
    ]);
    setRows(s.data ?? []); setTerms(t.data ?? []); setClasses(c.data ?? []);
  }, [active]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex justify-end"><StructureDialog terms={terms} classes={classes} onSaved={load} /></div>
      {rows.length === 0 ? <Empty body="No fee structures yet. Add one per term and class." /> : (
        <div className="overflow-x-auto rounded-2xl border border-border bg-card shadow-soft">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr><th className="px-4 py-2.5">Item</th><th className="px-4 py-2.5">Term</th><th className="px-4 py-2.5">Class</th><th className="px-4 py-2.5">Amount</th><th className="px-4 py-2.5">Due</th><th className="px-4 py-2.5" /></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="px-4 py-3 font-semibold">{r.name}{!r.is_mandatory && <Badge variant="outline" className="ml-2">Optional</Badge>}</td>
                  <td className="px-4 py-3 text-muted-foreground">{r.term?.name ?? "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{r.class?.name ?? "All"}</td>
                  <td className="px-4 py-3 font-mono">{fmt(Number(r.amount), r.currency)}</td>
                  <td className="px-4 py-3 text-muted-foreground">{r.due_date ?? "—"}</td>
                  <td className="px-4 py-3 text-right"><Button size="sm" variant="ghost" className="text-destructive" onClick={async () => {
                    if (!confirm("Delete fee item?")) return;
                    const { error } = await supabase.from("fee_structures").delete().eq("id", r.id);
                    if (error) toast.error(error.message); else load();
                  }}><Trash2 className="h-3.5 w-3.5" /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StructureDialog({ terms, classes, onSaved }: { terms: { id: string; name: string }[]; classes: { id: string; name: string }[]; onSaved: () => void }) {
  const { active } = useSchoolContext();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState<{ name: string; amount: string; category: typeof CATEGORIES[number]; term_id: string; class_id: string; due_date: string; is_mandatory: boolean }>({ name: "", amount: "", category: "tuition", term_id: "", class_id: "", due_date: "", is_mandatory: true });
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!active || !form.name || !form.amount) { toast.error("Name and amount required"); return; }
    setBusy(true);
    const { error } = await supabase.from("fee_structures").insert({
      school_id: active.school_id, name: form.name, amount: Number(form.amount),
      currency: active.school.currency, category: form.category,
      term_id: form.term_id || null, class_id: form.class_id || null,
      due_date: form.due_date || null, is_mandatory: form.is_mandatory,
    });
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Fee item added"); setOpen(false); onSaved();
    setForm({ name: "", amount: "", category: "tuition", term_id: "", class_id: "", due_date: "", is_mandatory: true });
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="bg-primary hover:bg-primary/90"><Plus className="mr-1.5 h-4 w-4" /> Fee item</Button></DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>New fee item</DialogTitle></DialogHeader>
        <form className="space-y-3" onSubmit={submit}>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name *"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Tuition" required /></Field>
            <Field label="Category">
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v as typeof form.category })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{CATEGORY_LABEL[c]}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label={`Amount (${active?.school.currency ?? "UGX"}) *`}><Input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required /></Field>
            <Field label="Due date"><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Term">
              <Select value={form.term_id} onValueChange={(v) => setForm({ ...form, term_id: v })}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{terms.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Class">
              <Select value={form.class_id} onValueChange={(v) => setForm({ ...form, class_id: v })}>
                <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <DialogFooter><Button type="submit" disabled={busy} className="bg-primary hover:bg-primary/90">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================ INVOICES ============================
function Invoices() {
  const { active } = useSchoolContext();
  const [rows, setRows] = useState<any[]>([]);
  const [learners, setLearners] = useState<{ id: string; first_name: string; last_name: string; admission_number: string | null }[]>([]);
  const [structures, setStructures] = useState<any[]>([]);

  const load = useCallback(async () => {
    if (!active) return;
    const [i, l, s] = await Promise.all([
      supabase.from("invoices").select("*, learner:learners(first_name,last_name,admission_number), term:terms(name)").eq("school_id", active.school_id).order("issue_date", { ascending: false }).limit(100),
      supabase.from("learners").select("id,first_name,last_name,admission_number").eq("school_id", active.school_id).eq("status","active").order("last_name"),
      supabase.from("fee_structures").select("*").eq("school_id", active.school_id),
    ]);
    setRows(i.data ?? []); setLearners(l.data ?? []); setStructures(s.data ?? []);
  }, [active]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex justify-end"><InvoiceDialog learners={learners} structures={structures} onSaved={load} /></div>
      {rows.length === 0 ? <Empty body="No invoices yet." /> : (
        <div className="overflow-x-auto rounded-2xl border border-border bg-card shadow-soft">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr><th className="px-4 py-2.5">Invoice</th><th className="px-4 py-2.5">Learner</th><th className="px-4 py-2.5">Total</th><th className="px-4 py-2.5">Paid</th><th className="px-4 py-2.5">Balance</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5" /></tr>
            </thead>
            <tbody>
              {rows.map((inv) => (
                <tr key={inv.id} className="border-t border-border">
                  <td className="px-4 py-3 font-mono text-xs">{inv.invoice_number}</td>
                  <td className="px-4 py-3 font-semibold">{inv.learner?.first_name} {inv.learner?.last_name}</td>
                  <td className="px-4 py-3 font-mono">{fmt(Number(inv.total_amount), inv.currency)}</td>
                  <td className="px-4 py-3 font-mono text-primary">{fmt(Number(inv.amount_paid), inv.currency)}</td>
                  <td className="px-4 py-3 font-mono">{fmt(Number(inv.balance), inv.currency)}</td>
                  <td className="px-4 py-3"><Badge className="capitalize">{inv.status}</Badge></td>
                  <td className="px-4 py-3 text-right">
                    <PayDialog invoice={inv} onSaved={load} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function InvoiceDialog({ learners, structures, onSaved }: { learners: { id: string; first_name: string; last_name: string; admission_number: string | null }[]; structures: any[]; onSaved: () => void }) {
  const { active } = useSchoolContext();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState<{ learner_id: string; due_date: string; selected: Set<string>; manual: { description: string; amount: string }[] }>({ learner_id: "", due_date: "", selected: new Set(), manual: [] });

  function toggleStruct(id: string) {
    const s = new Set(form.selected); if (s.has(id)) s.delete(id); else s.add(id);
    setForm({ ...form, selected: s });
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!active || !form.learner_id) { toast.error("Pick a learner"); return; }
    if (form.selected.size === 0 && form.manual.length === 0) { toast.error("Add at least one item"); return; }
    setBusy(true);
    const { data: numData } = await supabase.rpc("next_school_number", { _school: active.school_id, _key: "invoice_seq", _prefix: "INV" });
    const invoice_number = (numData as string) || `INV-${Date.now()}`;
    const { data: inv, error: e1 } = await supabase.from("invoices").insert({
      school_id: active.school_id, learner_id: form.learner_id, invoice_number,
      due_date: form.due_date || null, currency: active.school.currency, status: "sent",
    }).select("id").single();
    if (e1 || !inv) { setBusy(false); toast.error(e1?.message ?? "Failed"); return; }

    const items = [
      ...Array.from(form.selected).map((id) => {
        const s = structures.find((x) => x.id === id)!;
        return { invoice_id: inv.id, school_id: active.school_id, fee_structure_id: id, description: s.name, amount: Number(s.amount) };
      }),
      ...form.manual.filter((m) => m.description && m.amount).map((m) => ({ invoice_id: inv.id, school_id: active.school_id, description: m.description, amount: Number(m.amount) })),
    ];
    const { error: e2 } = await supabase.from("invoice_items").insert(items);
    setBusy(false);
    if (e2) { toast.error(e2.message); return; }
    toast.success("Invoice created"); setOpen(false); onSaved();
    setForm({ learner_id: "", due_date: "", selected: new Set(), manual: [] });
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="bg-primary hover:bg-primary/90"><Plus className="mr-1.5 h-4 w-4" /> Invoice</Button></DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>New invoice</DialogTitle></DialogHeader>
        <form className="space-y-3" onSubmit={submit}>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Learner *">
              <Select value={form.learner_id} onValueChange={(v) => setForm({ ...form, learner_id: v })}>
                <SelectTrigger><SelectValue placeholder="Pick" /></SelectTrigger>
                <SelectContent>{learners.map((l) => <SelectItem key={l.id} value={l.id}>{l.first_name} {l.last_name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Due date"><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></Field>
          </div>

          <div className="rounded-xl border border-border p-3">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Fee items</div>
            {structures.length === 0 && <div className="text-xs text-muted-foreground">No fee structures defined yet.</div>}
            <div className="max-h-44 space-y-1 overflow-y-auto">
              {structures.map((s) => (
                <label key={s.id} className="flex cursor-pointer items-center justify-between gap-3 rounded-lg px-2 py-1.5 hover:bg-muted">
                  <span className="flex items-center gap-2">
                    <input type="checkbox" checked={form.selected.has(s.id)} onChange={() => toggleStruct(s.id)} />
                    <span className="text-sm">{s.name}</span>
                  </span>
                  <span className="font-mono text-xs">{fmt(Number(s.amount), s.currency)}</span>
                </label>
              ))}
            </div>
            <div className="mt-2 space-y-2">
              {form.manual.map((m, i) => (
                <div key={i} className="flex gap-2">
                  <Input placeholder="Description" value={m.description} onChange={(e) => { const a = [...form.manual]; a[i].description = e.target.value; setForm({ ...form, manual: a }); }} />
                  <Input type="number" className="w-32" placeholder="Amount" value={m.amount} onChange={(e) => { const a = [...form.manual]; a[i].amount = e.target.value; setForm({ ...form, manual: a }); }} />
                </div>
              ))}
              <Button type="button" size="sm" variant="outline" onClick={() => setForm({ ...form, manual: [...form.manual, { description: "", amount: "" }] })}><Plus className="mr-1 h-3.5 w-3.5" /> Custom item</Button>
            </div>
          </div>

          <DialogFooter><Button type="submit" disabled={busy} className="bg-primary hover:bg-primary/90">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================ PAYMENTS ============================
function Payments() {
  const { active } = useSchoolContext();
  const [rows, setRows] = useState<any[]>([]);
  const load = useCallback(async () => {
    if (!active) return;
    const { data } = await supabase.from("payments").select("*, invoice:invoices(invoice_number, learner:learners(first_name,last_name))").eq("school_id", active.school_id).order("payment_date", { ascending: false }).limit(100);
    setRows(data ?? []);
  }, [active]);
  useEffect(() => { load(); }, [load]);

  return rows.length === 0 ? <Empty body="No payments recorded yet." /> : (
    <div className="overflow-x-auto rounded-2xl border border-border bg-card shadow-soft">
      <table className="w-full text-sm">
        <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
          <tr><th className="px-4 py-2.5">Receipt</th><th className="px-4 py-2.5">Date</th><th className="px-4 py-2.5">Learner</th><th className="px-4 py-2.5">Invoice</th><th className="px-4 py-2.5">Amount</th><th className="px-4 py-2.5">Method</th><th className="px-4 py-2.5" /></tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.id} className="border-t border-border">
              <td className="px-4 py-3 font-mono text-xs">{p.receipt_number}</td>
              <td className="px-4 py-3 text-muted-foreground">{p.payment_date}</td>
              <td className="px-4 py-3 font-semibold">{p.invoice?.learner?.first_name} {p.invoice?.learner?.last_name}</td>
              <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{p.invoice?.invoice_number}</td>
              <td className="px-4 py-3 font-mono text-primary">{fmt(Number(p.amount), active?.school.currency)}</td>
              <td className="px-4 py-3 capitalize text-muted-foreground">{p.method.replace("_"," ")}</td>
              <td className="px-4 py-3 text-right"><Button size="sm" variant="outline" onClick={() => printReceipt(p, active!.school)}><Printer className="mr-1 h-3.5 w-3.5" /> Receipt</Button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PayDialog({ invoice, onSaved }: { invoice: any; onSaved: () => void }) {
  const { active } = useSchoolContext();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState<{ amount: string; method: Database["public"]["Enums"]["payment_method"]; provider: string; reference: string; payer_phone: string; payer_name: string; notes: string }>({ amount: String(invoice.balance ?? 0), method: "cash", provider: "", reference: "", payer_phone: "", payer_name: "", notes: "" });
  if (Number(invoice.balance) <= 0) return <Button size="sm" variant="outline" disabled><Receipt className="h-3.5 w-3.5" /></Button>;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!active) return;
    const amt = Number(form.amount);
    if (!amt || amt <= 0) { toast.error("Amount required"); return; }
    setBusy(true);
    const { data: numData } = await supabase.rpc("next_school_number", { _school: active.school_id, _key: "receipt_seq", _prefix: "RCPT" });
    const receipt_number = (numData as string) || `RCPT-${Date.now()}`;
    const { data: pay, error } = await supabase.from("payments").insert({
      school_id: active.school_id, invoice_id: invoice.id, receipt_number,
      amount: amt, method: form.method, reference: form.reference || null,
      provider: form.provider || null, payer_phone: form.payer_phone || null, payer_name: form.payer_name || null,
      notes: form.notes || null,
    }).select("*").single();
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Payment recorded");
    setOpen(false);
    onSaved();
    printReceipt({ ...pay, invoice: { invoice_number: invoice.invoice_number, learner: invoice.learner } }, active.school);
  }

  const providerOptions = form.method === "mobile_money"
    ? [{ id: "mtn", label: "MTN MoMo" }, { id: "airtel", label: "Airtel Money" }]
    : form.method === "card"
      ? [{ id: "visa", label: "Visa" }, { id: "mastercard", label: "Mastercard" }]
      : form.method === "bank_transfer"
        ? [{ id: "bank", label: "Bank deposit / EFT" }]
        : [];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" className="bg-primary hover:bg-primary/90"><Receipt className="mr-1 h-3.5 w-3.5" /> Pay</Button></DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Record payment</DialogTitle></DialogHeader>
        <form className="space-y-3" onSubmit={submit}>
          <Field label={`Amount (${active?.school.currency}) *`}><Input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Method">
              <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v as typeof form.method, provider: "" })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["cash","mobile_money","bank_transfer","card","cheque"].map((m) => <SelectItem key={m} value={m}>{m.replace("_"," ")}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            {providerOptions.length > 0 && (
              <Field label="Provider">
                <Select value={form.provider} onValueChange={(v) => setForm({ ...form, provider: v })}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{providerOptions.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            )}
          </div>
          <Field label="Reference"><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="MTN txn, bank slip…" /></Field>
          {form.method === "mobile_money" && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Payer phone"><Input value={form.payer_phone} onChange={(e) => setForm({ ...form, payer_phone: e.target.value })} placeholder="+2567XXXXXXXX" /></Field>
              <Field label="Payer name"><Input value={form.payer_name} onChange={(e) => setForm({ ...form, payer_name: e.target.value })} /></Field>
            </div>
          )}
          <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          <DialogFooter><Button type="submit" disabled={busy} className="bg-primary hover:bg-primary/90">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Record &amp; print</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function printReceipt(p: any, school: { name: string; currency: string }) {
  const html = `<!doctype html><html><head><title>Receipt ${p.receipt_number}</title>
<style>body{font-family:system-ui,sans-serif;max-width:480px;margin:32px auto;padding:24px;color:#111}h1{margin:0;font-size:18px}h2{margin:0;font-size:24px;color:#059669}.row{display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px dashed #ccc}.muted{color:#666;font-size:12px}.big{font-size:22px;font-weight:800;color:#059669}</style></head>
<body>
<h1>${escapeHtml(school.name)}</h1><div class="muted">Official receipt</div>
<hr/>
<div class="row"><span>Receipt no.</span><b>${escapeHtml(p.receipt_number)}</b></div>
<div class="row"><span>Date</span><span>${escapeHtml(p.payment_date)}</span></div>
<div class="row"><span>Learner</span><span>${escapeHtml((p.invoice?.learner?.first_name ?? "") + " " + (p.invoice?.learner?.last_name ?? ""))}</span></div>
<div class="row"><span>Invoice</span><span>${escapeHtml(p.invoice?.invoice_number ?? "")}</span></div>
<div class="row"><span>Method</span><span style="text-transform:capitalize">${escapeHtml(String(p.method).replace("_"," "))}</span></div>
${p.reference ? `<div class="row"><span>Reference</span><span>${escapeHtml(p.reference)}</span></div>` : ""}
<div class="row"><span>Amount paid</span><span class="big">${fmt(Number(p.amount), school.currency)}</span></div>
${p.notes ? `<p class="muted">${escapeHtml(p.notes)}</p>` : ""}
<p class="muted" style="margin-top:24px">Generated by MAKAI · keep this receipt for your records.</p>
<script>window.onload=()=>window.print();</script>
</body></html>`;
  const w = window.open("", "_blank", "width=520,height=720"); if (!w) { toast.error("Pop-up blocked"); return; }
  w.document.write(html); w.document.close();
}

function escapeHtml(s: string) { return s.replace(/[&<>"']/g, (c) => ({ "&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;" }[c]!)); }
function fmt(n: number, ccy = "UGX") { return new Intl.NumberFormat(undefined, { style: "currency", currency: ccy || "UGX", maximumFractionDigits: 0 }).format(n); }
function Field({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) { return (<div className={"space-y-1.5 " + (className ?? "")}><Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</Label>{children}</div>); }
function Empty({ body }: { body: string }) { return <div className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">{body}</div>; }

// ============================ INTENTS (parent/learner submitted payments) ============================
function Intents() {
  const { active } = useSchoolContext();
  const [rows, setRows] = useState<any[]>([]);
  const [filter, setFilter] = useState<"pending" | "confirmed" | "rejected" | "all">("pending");

  const load = useCallback(async () => {
    if (!active) return;
    let q = supabase.from("payment_intents")
      .select("*, learner:learners(first_name,last_name,admission_number), invoice:invoices(invoice_number,balance,currency,learner:learners(first_name,last_name))")
      .eq("school_id", active.school_id).order("created_at", { ascending: false }).limit(200);
    if (filter !== "all") q = q.eq("status", filter);
    const { data } = await q;
    setRows(data ?? []);
  }, [active, filter]);
  useEffect(() => { load(); }, [load]);

  async function confirm(id: string) {
    const { error } = await supabase.rpc("confirm_payment_intent", { _intent: id });
    if (error) toast.error(error.message); else { toast.success("Payment confirmed — receipt issued"); load(); }
  }
  async function reject(id: string) {
    const reason = prompt("Reason for rejection?") ?? "";
    const { error } = await supabase.rpc("reject_payment_intent", { _intent: id, _reason: reason });
    if (error) toast.error(error.message); else { toast.success("Intent rejected"); load(); }
  }

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        {(["pending","confirmed","rejected","all"] as const).map((f) => (
          <Button key={f} size="sm" variant={filter === f ? "default" : "outline"} className={filter === f ? "bg-primary hover:bg-primary/90 capitalize" : "capitalize"} onClick={() => setFilter(f)}>{f}</Button>
        ))}
      </div>
      {rows.length === 0 ? <Empty body="No payment submissions to review." /> : (
        <div className="overflow-x-auto rounded-2xl border border-border bg-card shadow-soft">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr><th className="px-4 py-2.5">Date</th><th className="px-4 py-2.5">Learner</th><th className="px-4 py-2.5">Invoice</th><th className="px-4 py-2.5">Amount</th><th className="px-4 py-2.5">Provider</th><th className="px-4 py-2.5">Ref</th><th className="px-4 py-2.5">Payer</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5" /></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border align-top">
                  <td className="px-4 py-3 text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 font-semibold">{r.learner?.first_name} {r.learner?.last_name}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.invoice?.invoice_number}</td>
                  <td className="px-4 py-3 font-mono text-primary">{fmt(Number(r.amount), active?.school.currency)}</td>
                  <td className="px-4 py-3 text-xs capitalize text-muted-foreground">{(r.provider ?? r.method).replace("_"," ")}</td>
                  <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground">{r.reference ?? "—"}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{r.payer_phone ?? ""}{r.payer_name ? ` · ${r.payer_name}` : ""}</td>
                  <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ${r.status === "pending" ? "bg-amber-100 text-amber-700" : r.status === "confirmed" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>{r.status}</span></td>
                  <td className="px-4 py-3 text-right">
                    {r.status === "pending" && (
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="outline" className="text-emerald-700" onClick={() => confirm(r.id)}><CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Confirm</Button>
                        <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => reject(r.id)}><XCircle className="h-3.5 w-3.5" /></Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================ REMINDERS ============================
function Reminders() {
  const { active } = useSchoolContext();
  const [rows, setRows] = useState<any[]>([]);
  const [overdueInvoices, setOverdueInvoices] = useState<any[]>([]);

  const load = useCallback(async () => {
    if (!active) return;
    const [r, o] = await Promise.all([
      supabase.from("payment_reminders").select("*, learner:learners(first_name,last_name), invoice:invoices(invoice_number)").eq("school_id", active.school_id).order("created_at", { ascending: false }).limit(100),
      supabase.from("invoices").select("id,invoice_number,balance,due_date,learner_id,learner:learners(id,first_name,last_name,school_id)").eq("school_id", active.school_id).gt("balance", 0).in("status", ["sent","partial","overdue"]).order("due_date", { ascending: true }).limit(50),
    ]);
    setRows(r.data ?? []); setOverdueInvoices(o.data ?? []);
  }, [active]);
  useEffect(() => { load(); }, [load]);

  async function sendReminder(inv: any) {
    if (!active) return;
    const msg = `Reminder: Invoice ${inv.invoice_number} for ${inv.learner?.first_name} ${inv.learner?.last_name} has a balance of ${fmt(Number(inv.balance), active.school.currency)}${inv.due_date ? `. Due date was ${inv.due_date}.` : "."} Please pay via MTN MoMo, Airtel Money, card or bank.`;
    const { error } = await supabase.from("payment_reminders").insert({
      school_id: active.school_id, invoice_id: inv.id, learner_id: inv.learner_id,
      channel: "inapp", message: msg, status: "sent",
    });
    if (error) toast.error(error.message); else { toast.success("Reminder sent"); load(); }
  }

  async function sendAll() {
    if (!confirm(`Send reminder to ${overdueInvoices.length} families?`)) return;
    for (const inv of overdueInvoices) await sendReminder(inv);
  }

  return (
    <div className="space-y-6">
      <section>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-display text-base font-bold">Outstanding balances ({overdueInvoices.length})</h3>
          {overdueInvoices.length > 0 && <Button size="sm" className="bg-primary hover:bg-primary/90" onClick={sendAll}><Send className="mr-1.5 h-3.5 w-3.5" /> Remind all</Button>}
        </div>
        {overdueInvoices.length === 0 ? <Empty body="No outstanding invoices. 🎉" /> : (
          <div className="overflow-x-auto rounded-2xl border border-border bg-card shadow-soft">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground"><tr><th className="px-4 py-2.5">Learner</th><th className="px-4 py-2.5">Invoice</th><th className="px-4 py-2.5">Balance</th><th className="px-4 py-2.5">Due</th><th className="px-4 py-2.5" /></tr></thead>
              <tbody>
                {overdueInvoices.map((i) => (
                  <tr key={i.id} className="border-t border-border">
                    <td className="px-4 py-3 font-semibold">{i.learner?.first_name} {i.learner?.last_name}</td>
                    <td className="px-4 py-3 font-mono text-xs">{i.invoice_number}</td>
                    <td className="px-4 py-3 font-mono text-amber-600">{fmt(Number(i.balance), active?.school.currency)}</td>
                    <td className="px-4 py-3 text-muted-foreground">{i.due_date ?? "—"}</td>
                    <td className="px-4 py-3 text-right"><Button size="sm" variant="outline" onClick={() => sendReminder(i)}><BellRing className="mr-1 h-3.5 w-3.5" /> Remind</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <section>
        <h3 className="mb-3 font-display text-base font-bold">Recent reminders</h3>
        {rows.length === 0 ? <Empty body="No reminders sent yet." /> : (
          <ul className="space-y-2">
            {rows.map((r) => (
              <li key={r.id} className="rounded-xl border border-border bg-card p-3 text-sm">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{r.learner?.first_name} {r.learner?.last_name} · {r.invoice?.invoice_number}</span>
                  <span>{new Date(r.created_at).toLocaleString()}</span>
                </div>
                <div className="mt-1">{r.message}</div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

// ============================ REPORTS ============================
function Reports() {
  const { active } = useSchoolContext();
  const [data, setData] = useState<{
    invoices: any[]; payments: any[];
  } | null>(null);

  useEffect(() => {
    if (!active) return;
    (async () => {
      const [i, p] = await Promise.all([
        supabase.from("invoices").select("status,total_amount,amount_paid,balance,currency").eq("school_id", active.school_id),
        supabase.from("payments").select("amount,method,provider,payment_date").eq("school_id", active.school_id),
      ]);
      setData({ invoices: i.data ?? [], payments: p.data ?? [] });
    })();
  }, [active]);

  const stats = useMemo(() => {
    if (!data) return null;
    const billed = data.invoices.reduce((a, b) => a + Number(b.total_amount), 0);
    const collected = data.invoices.reduce((a, b) => a + Number(b.amount_paid), 0);
    const outstanding = data.invoices.reduce((a, b) => a + Number(b.balance), 0);
    const byMethod: Record<string, number> = {};
    const byProvider: Record<string, number> = {};
    const byMonth: Record<string, number> = {};
    for (const p of data.payments) {
      const m = String(p.method);
      byMethod[m] = (byMethod[m] ?? 0) + Number(p.amount);
      const prov = (p.provider ?? "other").toString();
      byProvider[prov] = (byProvider[prov] ?? 0) + Number(p.amount);
      const ym = String(p.payment_date).slice(0, 7);
      byMonth[ym] = (byMonth[ym] ?? 0) + Number(p.amount);
    }
    return { billed, collected, outstanding, byMethod, byProvider, byMonth };
  }, [data]);

  if (!stats) return <div className="grid place-items-center py-10 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  const ccy = active?.school.currency ?? "UGX";
  const months = Object.entries(stats.byMonth).sort(([a], [b]) => a.localeCompare(b)).slice(-12);
  const maxM = Math.max(1, ...months.map(([, v]) => v));

  return (
    <div className="space-y-6">
      <section className="grid gap-3 sm:grid-cols-3">
        <StatCard k="Total billed" v={fmt(stats.billed, ccy)} />
        <StatCard k="Collected" v={fmt(stats.collected, ccy)} accent="text-emerald-600" />
        <StatCard k="Outstanding" v={fmt(stats.outstanding, ccy)} accent={stats.outstanding > 0 ? "text-amber-600" : ""} />
      </section>

      <section className="grid gap-4 md:grid-cols-2">
        <div className="rounded-2xl border border-border bg-card p-5 shadow-soft">
          <h3 className="mb-3 font-display text-sm font-bold">Collections by method</h3>
          <ul className="space-y-2">
            {Object.entries(stats.byMethod).sort(([, a], [, b]) => b - a).map(([k, v]) => (
              <li key={k}><div className="flex justify-between text-xs"><span className="capitalize">{k.replace("_"," ")}</span><span className="font-mono">{fmt(v, ccy)}</span></div><div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted"><div className="h-full bg-primary" style={{ width: `${(v / Math.max(1, stats.collected)) * 100}%` }} /></div></li>
            ))}
            {Object.keys(stats.byMethod).length === 0 && <li className="text-sm text-muted-foreground">No payments yet.</li>}
          </ul>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5 shadow-soft">
          <h3 className="mb-3 font-display text-sm font-bold">Collections by provider</h3>
          <ul className="space-y-2">
            {Object.entries(stats.byProvider).sort(([, a], [, b]) => b - a).map(([k, v]) => (
              <li key={k}><div className="flex justify-between text-xs"><span className="uppercase">{k}</span><span className="font-mono">{fmt(v, ccy)}</span></div><div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted"><div className="h-full bg-gold" style={{ width: `${(v / Math.max(1, stats.collected)) * 100}%` }} /></div></li>
            ))}
            {Object.keys(stats.byProvider).length === 0 && <li className="text-sm text-muted-foreground">No payments yet.</li>}
          </ul>
        </div>
      </section>

      <section className="rounded-2xl border border-border bg-card p-5 shadow-soft">
        <h3 className="mb-3 font-display text-sm font-bold">Monthly collections</h3>
        {months.length === 0 ? <div className="text-sm text-muted-foreground">No data yet.</div> : (
          <div className="flex items-end gap-2 h-40">
            {months.map(([m, v]) => (
              <div key={m} className="flex flex-1 flex-col items-center gap-1">
                <div className="w-full rounded-t bg-primary" style={{ height: `${(v / maxM) * 100}%`, minHeight: "4px" }} title={fmt(v, ccy)} />
                <span className="text-[10px] text-muted-foreground">{m.slice(5)}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      <Button variant="outline" onClick={() => exportCSV(data!, ccy)}><FileText className="mr-1.5 h-4 w-4" /> Export payments CSV</Button>
    </div>
  );
}

function StatCard({ k, v, accent }: { k: string; v: string; accent?: string }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-soft">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{k}</div>
      <div className={`mt-1 font-display text-xl font-bold ${accent ?? ""}`}>{v}</div>
    </div>
  );
}

function exportCSV(d: { payments: any[] }, ccy: string) {
  const rows = [["Date","Method","Provider","Amount","Currency"], ...d.payments.map((p) => [p.payment_date, p.method, p.provider ?? "", String(p.amount), ccy])];
  const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = `payments-${new Date().toISOString().slice(0,10)}.csv`; a.click();
  URL.revokeObjectURL(url);
}
